import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
} from "react-table";
import { Table, Row, Col, Button, Input } from "reactstrap";
import { Filter, DefaultColumnFilter } from "./filters";
import JobListGlobalFilter from "../../components/Common/GlobalSearchFilter";

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  isJobListGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <React.Fragment>
      <Col md={4}>
        <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
          <div className="position-relative">
            <label htmlFor="search-bar-0" className="search-label">
              <span id="search-bar-0-label" className="sr-only">
                Search this table
              </span>
              <input
                onChange={(e) => {
                  setValue(e.target.value);
                  onChange(e.target.value);
                }}
                id="search-bar-0"
                type="text"
                className="form-control"
                placeholder={`${count} records...`}
                value={value || ""}
              />
            </label>
            <i className="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </Col>
      {isJobListGlobalFilter && <JobListGlobalFilter />}
    </React.Fragment>
  );
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isJobListGlobalFilter,
  isAddOptions,
  isAddUserList,
  handleOrderClicks,
  handleUserClick,
  handleCustomerClick,
  isAddCustList,
  customPageSize,
  className,
  customPageSizeOptions,
  pagination,
  paginationServer,
  paginationTotalRows,
  onPageChange,
  currentPage,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
    isProductTable = true
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: currentPage - 1 || 0,
        pageSize: customPageSize,
        sortBy: [
          {
            desc: true,
          },
        ],
      },
      manualPagination: paginationServer,
      pageCount: paginationServer ? Math.ceil(paginationTotalRows / customPageSize) : undefined,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : "";
  };

  const onChangeInSelect = (event) => {
    setPageSize(Number(event.target.value));
  };
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const onChangeInInput = (event) => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };

  const handlePageChange = (newPage) => {
    if (paginationServer) {
      onPageChange(newPage + 1);
    } else {
      gotoPage(newPage);
    }
  };

  return (
    <Fragment>
      <Row className="mb-2">
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isJobListGlobalFilter={isJobListGlobalFilter}
          />
        )}
        {isAddOptions && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded  mb-2 me-2"
                onClick={handleOrderClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New Order
              </Button>
            </div>
          </Col>
        )}
        {isAddUserList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="primary"
                className="btn mb-2 me-2"
                onClick={handleUserClick}
              >
                <i className="mdi mdi-plus-circle-outline me-1" />
                Create New User
              </Button>
            </div>
          </Col>
        )}
        {isAddCustList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={handleCustomerClick}
              >
                <i className="mdi mdi-plus me-1" />
                New Customers
              </Button>
            </div>
          </Col>
        )}
      </Row>

      <div className="table-responsive react-table">
        <Table bordered hover {...getTableProps()} className={className}>
          <thead className="table-light table-nowrap">
            {headerGroups.map((headerGroup) => {
              const headerGroupProps = headerGroup.getHeaderGroupProps();
              const { key, ...restHeaderGroupProps } = headerGroupProps;

              return (
                <tr key={key} {...restHeaderGroupProps}>
                  {headerGroup.headers.map((column) => (
                    <th key={column.id}>
                      <div className="mb-2" {...column.getSortByToggleProps()}>
                        {column.render("Header")}
                        {generateSortingIndicator(column)}
                      </div>
                      <Filter column={column} />
                    </th>
                  ))}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const rowProps = row.getRowProps();
              const { key, ...restRowProps } = rowProps;

              return (
                <Fragment key={key}>
                  <tr
                    {...restRowProps}
                    className="border-b border-gray-200 hover:bg-gray-100 transition-all"
                  >
                    {row.cells.map((cell) => {
                      const cellProps = cell.getCellProps();
                      const { key, ...restCellProps } = cellProps;

                      return (
                        <td
                          key={key}
                          {...restCellProps}
                          className="px-4 py-3 text-gray-700"
                          style={{
                            backgroundColor:
                              cell?.row?.original?.shippingAddress?.postalCode === "132103"
                                ? "#c2f7ce"
                                : "transparent",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td colSpan={row.cells.length} >
                      <div>
                        {row?.original?.orders && row?.original?.orders?.map((item, index) => (
                          <div
                            style={{ display: "flex", flexWrap: "wrap" }}
                            key={item._id}
                            className="flex flex-wrap items-center gap-3 border border-gray rounded-lg w-full bg-white p-2"
                          >
                            <div>
                              <img
                                src={item.product.images[0].startsWith("https") ? item.product.images[0] : `${process.env.REACT_APP_BASEURL}${item.product.images[0]}`}
                                alt={item.product.name}
                                className="w-20 h-16 object-cover rounded-md"
                                style={{ height: 40, width: 40 }}
                              />
                            </div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "90%" }}>
                              <div style={{ width: "30%" }} className="font-medium text-gray-900">
                                {item.product.name}
                              </div>
                              <div style={{ width: "30%", display: "flex", justifyContent: "end" }} className="flex justify-end font-medium text-gray-900">
                                Volumetric: {item.product.volumetricWeight}
                              </div>
                              <div style={{ width: "30%", display: "flex", justifyContent: "end" }} className="text-sm text-gray-500">
                                Qty: {item.quantity} × {formatCurrency(item.product.price)}
                                {item.colors && (
                                  <span className="ml-2">
                                    Color:{" "}
                                    <span
                                      className="inline-block w-4 h-4 rounded-full"
                                      style={{ backgroundColor: item.colors }}
                                    ></span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {row?.original?.orders && row?.original?.orders?.length > 0 &&
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }} className="p-2 rounded-lg">
                            <div style={{ width: "30%" }}></div>
                            <div style={{ width: "30%", display: "flex", justifyContent: "end" }} className="font-medium text-gray-900">
                              Volumetric Sum: {Number.isInteger(row?.original?.orders?.reduce((sum, item) => sum + (item.product.volumetricWeight || 0), 0).toFixed(2)) ? row?.original?.orders?.reduce((sum, item) => sum + (item.product.volumetricWeight || 0).toFixed(2), 0) : (row?.original?.orders?.reduce((sum, item) => sum + (item?.product?.volumetricWeight || 0), 0))?.toFixed(2)} KG
                            </div>
                            <div style={{ width: "30%", display: "flex", justifyContent: "end" }} className="font-medium text-gray-900">
                              Total Qty: {row?.original?.orders?.reduce((sum, item) => sum + item.quantity, 0)} X
                              Total Price: {formatCurrency(row?.original?.orders?.reduce((sum, item) => sum + item.quantity * item.product.price, 0))}
                            </div>
                          </div>
                        }
                      </div>
                    </td>
                  </tr>

                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>

      {pagination && (
        <Row className="justify-content-md-end justify-content-center align-items-center">
          <Col className="col-md-auto">
            <div className="d-flex gap-1">
              <Button
                color="primary"
                onClick={() => handlePageChange(0)}
                disabled={!canPreviousPage}
              >
                {"<<"}
              </Button>
              <Button
                color="primary"
                onClick={() => handlePageChange(pageIndex - 1)}
                disabled={!canPreviousPage}
              >
                {"<"}
              </Button>
            </div>
          </Col>
          <Col className="col-md-auto d-none d-md-block">
            Page{" "}
            <strong>
              {pageIndex + 1} of {paginationServer ? Math.ceil(paginationTotalRows / pageSize) : pageOptions.length}
            </strong>
          </Col>
          <Col className="col-md-auto">
            <Input
              type="number"
              min={1}
              style={{ width: 70 }}
              max={paginationServer ? Math.ceil(paginationTotalRows / pageSize) : pageOptions.length}
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                handlePageChange(page);
              }}
            />
          </Col>

          <Col className="col-md-auto">
            <div className="d-flex gap-1">
              <Button
                color="primary"
                onClick={() => handlePageChange(pageIndex + 1)}
                disabled={!canNextPage}
              >
                {">"}
              </Button>
              <Button
                color="primary"
                onClick={() => handlePageChange(paginationServer ? Math.ceil(paginationTotalRows / pageSize) - 1 : pageCount - 1)}
                disabled={!canNextPage}
              >
                {">>"}
              </Button>
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;
